import { useCallback, useState } from 'react';
import { useMachine } from '@xstate/react';
import { userAgent } from '@tink/tink-link-core/src/user-agent-detector';
import {
  BankIdMachineContext,
  BankIdMachineProps,
  createBankIdMachine,
} from '../machines/bankIdMachine/bankIdMachine';
import { BankIdRedirector } from '../../utilities/bankid-redirector/bankid-redirector';
import { shouldRedirectToReferPage } from '../../models/configuration';
import { useConfiguration } from '../../components/ApplicationProviders/ConfigurationProvider/ConfigurationProvider';
import { useProduct } from '../../components/ApplicationProviders/ProductProvider/ProductProvider';
import { CredentialsEntity } from '../../models/credentials';
import { ApplicationEvent, PageView, UserInteractionEvent } from '../../models/analytics';
import { useAnalytics } from '../../core/Analytics/AnalyticsProvider';
import { getSafariEndFlowStrategy } from '../screens/third-party-redirect/thirdPartyRedirectHelpers';

export type UseBankIdMachineProps = Pick<BankIdMachineProps, 'onDone'> &
  Pick<BankIdMachineContext, 'bankIdTarget' | 'credentials'>;

export const useBankIdMachine = ({ bankIdTarget, onDone, credentials }: UseBankIdMachineProps) => {
  const { data } = useConfiguration();
  const { productType } = useProduct();
  const { tinklinkAnalytics } = useAnalytics();
  const onAutoOpenBankIdWithAnalytics = useCallback(
    (credentialsChanged: CredentialsEntity) => {
      tinklinkAnalytics.sendInteractionEvent({
        action: `${ApplicationEvent.BANK_ID_MACHINE}/${UserInteractionEvent.OPEN_BANKID}`,
        view: PageView.BANKID_LOCAL_AWAITING_SCREEN,
      });

      new BankIdRedirector({
        credentials: credentialsChanged,
        productType,
        redirectToReferPage: shouldRedirectToReferPage(data),
        enableCredentialsMachine: true,
      }).open();
    },
    [data, productType, tinklinkAnalytics]
  );
  // this check would guard us from showing FinishedFlow on iphone but excluding embedded apps or iframes
  const canFinishFlow = getSafariEndFlowStrategy();
  const [machine] = useState(
    createBankIdMachine({
      initialContext: {
        credentials,
        bankIdTarget,
        isMobile: userAgent.isMobile,
        onAutoOpenBankIdWithAnalytics,
        userClickedOpenBankId: false,
        canFinishFlow,
      },
      onDone,
    })
  );

  return useMachine(machine);
};
